import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  useTheme,
  Tooltip,
  alpha,
  Container,
  ListItemIcon,
  useMediaQuery,
  Divider,
} from '@mui/material';
import {
  Brightness4,
  Brightness7,
  AccountCircle,
  Settings,
  Logout,
  Menu as MenuIcon,
  History,
  Groups, 
  Person,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';
import { Explore as ExploreIcon } from '@mui/icons-material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

// Import API
import { adminApi } from '../services/api';

// Import your logo
import logo from '../assets/WFHCafe_WObg.png';

const Navbar: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  
  const { user, logout } = useAuth();
  const { mode, toggleTheme } = useCustomTheme();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check admin status when user changes
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user?._id) {
        try {
          setLoading(true);
          const response = await adminApi.checkAdminStatus();
          
          if (response.success && response.adminIds) {
            const isUserAdmin = response.adminIds.includes(user._id);
            setIsAdmin(isUserAdmin);
          } else {
            setIsAdmin(false);
          }
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        } finally {
          setLoading(false);
        }
      } else {
        setIsAdmin(false);
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [user?._id]);

  // Profile menu handlers - UPDATED: No redirect on profile pic click
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  // Mobile menu handlers
  const handleOpenMobileMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleCloseMobileMenu = () => {
    setMobileMenuAnchor(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setIsAdmin(false);
      toast.success('Logged out successfully');
      navigate('/');
      handleProfileMenuClose();
      handleCloseMobileMenu();
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const handleSettings = () => {
    navigate('/profile');
    handleProfileMenuClose();
    handleCloseMobileMenu();
  };

  const handlePublicProfile = () => {
    if (user?.username) {
      navigate(`/${user.username}`);
      handleProfileMenuClose();
      handleCloseMobileMenu();
    }
  };

  const handleLogin = () => {
    navigate('/login');
    handleCloseMobileMenu();
  };

  const handleRegister = () => {
    navigate('/register');
    handleCloseMobileMenu();
  };

  const handleExplore = () => {
    navigate('/explore');
    handleCloseMobileMenu();
  };

  const handleCommunity = () => {
    navigate('/community');
    handleCloseMobileMenu();
  };

  const handleAdmin = () => {
    navigate('/admin');
    handleProfileMenuClose();
    handleCloseMobileMenu();
  };

  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';
  const goldColor = '#C4B66F';

  const isExploreActive = location.pathname === '/explore';
  const isPreviouslyViewedActive = location.pathname === '/previously-viewed';
  const isCommunityActive = location.pathname === '/community' || location.pathname.startsWith('/community/');
  const isAdminActive = location.pathname === '/admin';

  // Don't show Admin button if still loading or not admin
  const showAdminButton = !loading && isAdmin && user;

  return (
    <AppBar 
      position="sticky" 
      elevation={0}
      sx={{ 
        background: mode === 'dark' 
          ? lokiGradient
          : (isScrolled ? 'rgba(255, 255, 255, 0.8)' : 'transparent'),
        backdropFilter: 'blur(10px)',
        borderBottom: isScrolled ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
        color: mode === 'dark' ? '#E0E0E0' : '#051814',
        transition: 'all 0.3s ease',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Logo and App Name - Now aligned to left */}
          <Box 
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="WFHCafe Logo"
              sx={{
                height: 40,
                width: 'auto',
                transition: 'all 0.3s ease',
                filter: mode === 'dark' ? 'brightness(1.1)' : 'none',
                '&:hover': {
                  transform: 'scale(1.05)',
                }
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                letterSpacing: '0.05em',
                '&:hover': {
                  color: goldColor,
                },
                transition: '0.3s'
              }}
            >
              WFHCafe
            </Typography>
          </Box>

          {/* Spacer to push other items to right */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Desktop Navigation - Hidden on Mobile */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            alignItems: 'center', 
            gap: 3,
            mr: 2
          }}>
            {/* Explore Button */}
            <Button
              component={RouterLink}
              to="/explore"
              sx={{
                color: isExploreActive ? goldColor : 'inherit',
                textTransform: 'none',
                fontWeight: 800,
                fontSize: '1.3rem',
                position: 'relative',
                px: 0,
                minWidth: 'auto',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: isExploreActive ? '100%' : '0%',
                  height: '2px',
                  backgroundColor: goldColor,
                  transition: 'width 0.3s ease',
                },
                '&:hover::after': {
                  width: '100%',
                },
                '&:hover': {
                  color: goldColor,
                  backgroundColor: 'transparent',
                },
              }}
            >
              Explore
            </Button>

            {/* Previously Viewed Button */}
            <Button
              component={RouterLink}
              to="/previously-viewed"
              sx={{
                color: isPreviouslyViewedActive ? goldColor : 'inherit',
                textTransform: 'none',
                fontWeight: 800,
                fontSize: '1.3rem',
                position: 'relative',
                px: 0,
                minWidth: 'auto',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: isPreviouslyViewedActive ? '100%' : '0%',
                  height: '2px',
                  backgroundColor: goldColor,
                  transition: 'width 0.3s ease',
                },
                '&:hover::after': {
                  width: '100%',
                },
                '&:hover': {
                  color: goldColor,
                  backgroundColor: 'transparent',
                },
              }}
            >
              Previously Viewed
            </Button>

            {/* Community Button */}
            <Button
              component={RouterLink}
              to="/community"
              sx={{
                color: isCommunityActive ? goldColor : 'inherit',
                textTransform: 'none',
                fontWeight: 800,
                fontSize: '1.3rem',
                position: 'relative',
                px: 0,
                minWidth: 'auto',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: isCommunityActive ? '100%' : '0%',
                  height: '2px',
                  backgroundColor: goldColor,
                  transition: 'width 0.3s ease',
                },
                '&:hover::after': {
                  width: '100%',
                },
                '&:hover': {
                  color: goldColor,
                  backgroundColor: 'transparent',
                },
              }}
            >
              Community
            </Button>

            {/* Admin Button - Only shown if user is admin (stays outside with Explore) */}
            {showAdminButton && (
              <Button
                component={RouterLink}
                to="/admin"
                sx={{
                  color: isAdminActive ? goldColor : 'inherit',
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: '1.3rem',
                  position: 'relative',
                  px: 0,
                  minWidth: 'auto',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: isAdminActive ? '100%' : '0%',
                    height: '2px',
                    backgroundColor: goldColor,
                    transition: 'width 0.3s ease',
                  },
                  '&:hover::after': {
                    width: '100%',
                  },
                  '&:hover': {
                    color: goldColor,
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Admin
              </Button>
            )}

            <Tooltip title="Toggle Theme">
              <IconButton onClick={toggleTheme} color="inherit">
                {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
            </Tooltip>

            {user ? (
              <>
                {/* Profile Avatar - UPDATED: Click opens menu, doesn't redirect */}
                <IconButton
                  onClick={handleProfileMenuOpen}
                  sx={{ 
                    p: 0,
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }}
                >
                  {user.profilePicture ? (
                    <Avatar
                      src={user.profilePicture}
                      alt={user.username}
                      sx={{ width: 45, height: 45 }}
                    />
                  ) : (
                    <AccountCircle sx={{ width: 45, height: 45 }} />
                  )}
                </IconButton>
                
                {/* Profile Menu */}
                <Menu
                  sx={{ 
                    mt: '45px',
                    '& .MuiPaper-root': {
                      borderRadius: '20px',
                      minWidth: 220,
                      bgcolor: mode === 'dark' ? '#051814' : '#fff',
                      border: `1px solid ${mode === 'dark' ? alpha(goldColor, 0.2) : '#eee'}`,
                      color: mode === 'dark' ? '#fff' : '#000',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                      mt: 1,
                    }
                  }}
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleProfileMenuClose}
                >
                  {/* User Profile Section - Clickable to go to public profile */}
                  <MenuItem 
                    onClick={handlePublicProfile}
                    sx={{ 
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&:hover': {
                        backgroundColor: alpha(goldColor, 0.1),
                      }
                    }}
                  >
                    {user.profilePicture ? (
                      <Avatar
                        src={user.profilePicture}
                        alt={user.username}
                        sx={{ width: 36, height: 36 }}
                      />
                    ) : (
                      <AccountCircle sx={{ width: 36, height: 36, color: mode === 'dark' ? goldColor : '#051814' }} />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {user.username}
                      </Typography>
                      <Typography variant="caption" sx={{ color: mode === 'dark' ? alpha('#fff', 0.6) : alpha('#051814', 0.6) }}>
                        {user.email}
                      </Typography>
                    </Box>
                    {/* <Person fontSize="small" sx={{ color: alpha(mode === 'dark' ? goldColor : '#051814', 0.6) }} /> */}
                  </MenuItem>

                  <Divider sx={{ my: 0.5, borderColor: alpha(mode === 'dark' ? goldColor : '#051814', 0.1) }} />

                  {/* Settings */}
                  <MenuItem 
                    onClick={handleSettings}
                    sx={{ 
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: alpha(goldColor, 0.1),
                      }
                    }}
                  >
                    <ListItemIcon>
                      <Settings fontSize="small" sx={{ color: mode === 'dark' ? goldColor : '#051814' }} />
                    </ListItemIcon>
                    <Typography>Settings</Typography>
                  </MenuItem>

                  {/* Logout */}
                  <MenuItem 
                    onClick={handleLogout}
                    sx={{ 
                      py: 1.5,
                      color: '#ff4444',
                      '&:hover': {
                        backgroundColor: alpha('#ff4444', 0.1),
                      }
                    }}
                  >
                    <ListItemIcon>
                      <Logout fontSize="small" sx={{ color: '#ff4444' }} />
                    </ListItemIcon>
                    <Typography>Logout</Typography>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="outlined"
                  sx={{ 
                    borderRadius: 20, 
                    textTransform: 'none',
                    borderColor: 'inherit',
                    color: 'inherit',
                    '&:hover': {
                      borderColor: goldColor,
                      color: goldColor
                    }
                  }}
                >
                  Login
                </Button>
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  sx={{ 
                    borderRadius: 20, 
                    textTransform: 'none', 
                    background: mode === 'dark' ? 'rgba(255,255,255,0.1)' : lokiGradient,
                    color: mode === 'dark' ? '#fff' : goldColor,
                    fontWeight: 'bold',
                    border: '1px solid rgba(196, 182, 111, 0.3)'
                  }}
                >
                  Sign Up
                </Button>
              </Box>
            )}
          </Box>

          {/* Mobile Navigation */}
          <Box sx={{ 
            display: { xs: 'flex', md: 'none' }, 
            alignItems: 'center', 
            gap: 1.5 
          }}>
            
            <Tooltip title="Toggle Theme">
              <IconButton onClick={toggleTheme} color="inherit" size="small">
                {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
            </Tooltip>

            {/* Hamburger menu for ALL users on mobile */}
            <IconButton
              color="inherit"
              onClick={handleOpenMobileMenu}
              sx={{
                '&:hover': {
                  color: goldColor,
                }
              }}
            >
              <MenuIcon />
            </IconButton>

            {/* Mobile Menu for ALL Users */}
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={handleCloseMobileMenu}
              sx={{
                '& .MuiPaper-root': {
                  borderRadius: '20px',
                  minWidth: 200,
                  bgcolor: mode === 'dark' ? '#051814' : '#fff',
                  border: `1px solid ${mode === 'dark' ? alpha(goldColor, 0.2) : '#eee'}`,
                  color: mode === 'dark' ? '#fff' : '#000',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                  mt: 1,
                }
              }}
            >
              {user ? (
                <>
                  {/* User Profile Section in Mobile Menu */}
                  <MenuItem 
                    onClick={handlePublicProfile}
                    sx={{ 
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      borderBottom: `1px solid ${alpha(mode === 'dark' ? goldColor : '#051814', 0.1)}`,
                    }}
                  >
                    {user.profilePicture ? (
                      <Avatar
                        src={user.profilePicture}
                        alt={user.username}
                        sx={{ width: 36, height: 36 }}
                      />
                    ) : (
                      <AccountCircle sx={{ width: 36, height: 36 }} />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2">{user.username}</Typography>
                      <Typography variant="caption">{user.email}</Typography>
                    </Box>
                    {/* <Person fontSize="small" /> */}
                  </MenuItem>

                  {/* Admin Panel (only for admins, in mobile menu) */}
                  {/* {isAdmin && (
                    <MenuItem onClick={handleAdmin}>
                      <ListItemIcon>
                        <AdminPanelSettingsIcon fontSize="small" />
                      </ListItemIcon>
                      Admin Panel
                    </MenuItem>
                  )} */}

                  <MenuItem onClick={handleSettings}>
                    <ListItemIcon>
                      <Settings fontSize="small" />
                    </ListItemIcon>
                    Settings
                  </MenuItem>

                  <MenuItem onClick={handleLogout} sx={{ color: '#ff4444' }}>
                    <ListItemIcon>
                      <Logout fontSize="small" sx={{ color: '#ff4444' }} />
                    </ListItemIcon>
                    Logout
                  </MenuItem>

                  <Divider />
                </>
              ) : (
                <>
                  <MenuItem onClick={handleLogin}>
                    <ListItemIcon>
                      <AccountCircle fontSize="small" />
                    </ListItemIcon>
                    Login
                  </MenuItem>

                  <MenuItem onClick={handleRegister}>
                    <ListItemIcon>
                      <Settings fontSize="small" />
                    </ListItemIcon>
                    Sign Up
                  </MenuItem>
                  <Divider />
                </>
              )}

              {/* Mobile Explore menu item */}
              <MenuItem 
                onClick={handleExplore}
                sx={{ 
                  py: 1.5,
                  color: isExploreActive ? goldColor : 'inherit',
                  fontWeight: isExploreActive ? 600 : 'normal'
                }}
              >
                <ListItemIcon>
                  <ExploreIcon fontSize="small" sx={{ color: isExploreActive ? goldColor : (mode === 'dark' ? goldColor : 'inherit') }} />
                </ListItemIcon>
                Explore
              </MenuItem>

              {/* Mobile Previously Viewed menu item */}
              <MenuItem 
                onClick={() => {
                  navigate('/previously-viewed');
                  handleCloseMobileMenu();
                }}
                sx={{ 
                  py: 1.5,
                  color: isPreviouslyViewedActive ? goldColor : 'inherit',
                  fontWeight: isPreviouslyViewedActive ? 600 : 'normal'
                }}
              >
                <ListItemIcon>
                  <History fontSize="small" sx={{ 
                    color: isPreviouslyViewedActive ? goldColor : (mode === 'dark' ? goldColor : 'inherit') 
                  }} />
                </ListItemIcon>
                Previously Viewed
              </MenuItem>

              {/* Mobile Community menu item */}
              <MenuItem 
                onClick={handleCommunity}
                sx={{ 
                  py: 1.5,
                  color: isCommunityActive ? goldColor : 'inherit',
                  fontWeight: isCommunityActive ? 600 : 'normal'
                }}
              >
                <ListItemIcon>
                  <Groups fontSize="small" sx={{ 
                    color: isCommunityActive ? goldColor : (mode === 'dark' ? goldColor : 'inherit') 
                  }} />
                </ListItemIcon>
                Community
              </MenuItem>

              {/* Mobile Admin menu item (only for admins) */}
              {isAdmin && (
                <MenuItem 
                  onClick={handleAdmin}
                  sx={{ 
                    py: 1.5,
                    color: isAdminActive ? goldColor : 'inherit',
                    fontWeight: isAdminActive ? 600 : 'normal'
                  }}
                >
                  <ListItemIcon>
                    <AdminPanelSettingsIcon fontSize="small" sx={{ 
                      color: isAdminActive ? goldColor : (mode === 'dark' ? goldColor : 'inherit') 
                    }} />
                  </ListItemIcon>
                  Admin
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;